import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Avatar from "@/components/Avatar";
import { api, EMOTION_TAGS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HeartIcon, TrashIcon, PlusIcon } from "@phosphor-icons/react";

const DEFAULT = { title: "", team: "", player: "", game_date: "", description: "", emotion_tag: "hype" };

export default function Memory() {
  const { user } = useAuth();
  const [memories, setMemories] = useState([]);
  const [form, setForm] = useState(DEFAULT);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    const r = await api.get("/memories");
    setMemories(r.data || []);
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/memories", form);
      setForm(DEFAULT);
      setOpen(false);
      await refresh();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this memory?")) return;
    await api.delete(`/memories/${id}`);
    await refresh();
  };

  return (
    <AppLayout>
      <div className="mb-6 grid md:grid-cols-3 gap-6 fade-up">
        <div className="md:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#32D74B] mb-2">Fan Memory</p>
          <h1 className="font-display font-bold text-4xl md:text-5xl uppercase tracking-tighter">Moments You'll Never Forget</h1>
          <p className="text-white/60 text-sm mt-2">Save the games, players, and moments that defined your fandom.</p>
        </div>
        <div className="p-5 rounded-md bg-[#121212] border border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <Avatar src={user?.picture} name={user?.name} size={48} />
            <div>
              <p className="font-display font-bold uppercase tracking-tight">{user?.name}</p>
              <p className="text-xs text-white/50">{user?.email}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Mini label="Teams" value={user?.favorite_teams?.length || 0} />
            <Mini label="Players" value={user?.favorite_players?.length || 0} />
            <Mini label="Plan" value={(user?.plan || "FREE").toUpperCase()} />
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end mb-4">
        <button
          data-testid="add-memory-btn"
          onClick={() => setOpen(true)}
          className="px-4 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5"
        >
          <PlusIcon weight="bold" /> Add Memory
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {memories.map((m) => (
          <div key={m.id} className="p-5 rounded-md bg-[#121212] border border-white/10" data-testid={`memory-${m.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#32D74B]">{m.emotion_tag}</span>
              <button onClick={() => remove(m.id)} data-testid={`memory-delete-${m.id}`} className="text-white/40 hover:text-[#FF453A]"><TrashIcon size={14} /></button>
            </div>
            <h3 className="font-display font-bold text-xl uppercase mb-1 tracking-tight">{m.title}</h3>
            <p className="text-xs text-white/40 mb-2">{m.team} {m.player ? `· ${m.player}` : ""} {m.game_date ? `· ${m.game_date}` : ""}</p>
            <p className="text-sm text-white/80">{m.description}</p>
          </div>
        ))}
        {memories.length === 0 && (
          <div className="md:col-span-3 p-10 rounded-md bg-[#121212] border border-white/10 text-center">
            <HeartIcon weight="bold" size={32} className="text-[#FF453A] mx-auto mb-3" />
            <p className="text-white/70 font-medium">No memories yet — capture your first one.</p>
          </div>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            data-testid="add-memory-modal"
            className="w-full max-w-lg rounded-md bg-[#121212] border border-white/10 p-6 space-y-3"
          >
            <h2 className="font-display font-bold text-2xl uppercase tracking-tighter">Add Memory</h2>
            <input data-testid="memory-title" required placeholder="Title (Game 7 buzzer-beater)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="memory-team" placeholder="Team" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} className="bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
              <input data-testid="memory-player" placeholder="Player" value={form.player} onChange={(e) => setForm({ ...form, player: e.target.value })} className="bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            </div>
            <input data-testid="memory-date" type="date" value={form.game_date} onChange={(e) => setForm({ ...form, game_date: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm" />
            <textarea data-testid="memory-description" required placeholder="What happened? Why does it still hit?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full bg-black border border-white/15 rounded-md px-3 py-2 text-sm min-h-[90px]" />
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/40 mb-2 block">Emotion Tag</label>
              <div className="flex flex-wrap gap-2">
                {EMOTION_TAGS.map((t) => (
                  <button type="button" key={t.value} data-testid={`emotion-${t.value}`} onClick={() => setForm({ ...form, emotion_tag: t.value })} className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border ${form.emotion_tag === t.value ? "border-[#32D74B] bg-[#32D74B]/10 text-[#32D74B]" : "border-white/15 text-white/60"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-md border border-white/15 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white">Cancel</button>
              <button type="submit" disabled={saving} data-testid="memory-submit" className="px-4 py-2 rounded-md bg-[#0A84FF] hover:bg-blue-600 text-xs font-bold uppercase tracking-wider">{saving ? "Saving…" : "Save memory"}</button>
            </div>
          </form>
        </div>
      )}
    </AppLayout>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded bg-black/40 border border-white/5 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-white/40">{label}</p>
      <p className="font-num text-xl font-black">{value}</p>
    </div>
  );
}
